/**
 * The Admin Configuration screen (f2.1) -- the project's first form.
 *
 * Mirrors the prototype's shape (settings.html): a page head with Discard and
 * Save changes ABOVE a tab strip, one tab per section, a `card` per section
 * and a `grid grid-2` of fields. The inventory of keys is NOT the mock's: it
 * is whatever the server returns, so a later phase adds a section by inserting
 * rows and this file never changes (BR-F2.1-11).
 *
 * The idioms every later editing screen inherits start here:
 *
 * - controls derived from declared metadata, never hand-written per field;
 * - page-level dirty state across tabbed sections -- switching tabs never
 *   discards an edit, because the buttons that save and discard sit above the
 *   tabs and act on all of them;
 * - an all-or-nothing save that sends only the changed keys;
 * - server errors bound back to their fields by key, with the tab holding the
 *   first one selected, and the pending edits kept;
 * - a prompt before unsaved edits are lost by leaving.
 *
 * Visibility comes from the server's navigation list. Hiding the screen is
 * presentation only: the SERVER refuses every non-Admin, and records it.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader2,
  RotateCcw,
  RotateCw,
  Shield,
  ShieldAlert,
  SlidersHorizontal,
  XCircle,
} from 'lucide-react';

import { ApiError, type SettingsCatalog } from '@/api/client';
import { useSaveSettings, useSettingsCatalog } from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';

import { sectionIcon } from './sectionIcon';
import { SettingField } from './SettingField';
import {
  errorsByKey,
  firstSectionWithProblem,
  isChanged,
  localProblems,
  pendingBySection,
  pendingChanges,
  type Draft,
} from './settingsForm';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

/** The navigation key the server grants to Platform Admin alone. */
const NAV_KEY = 'configuration';

const LEAVE_PROMPT = 'You have unsaved settings. Leave this page and discard them?';

// --- Page chrome ------------------------------------------------------------

/**
 * The page head.
 *
 * The Admin badge is ALWAYS rendered, beside whatever actions the screen
 * passes in rather than instead of them: the restriction is a property of the
 * page, not a placeholder for an empty action bar, and it disappearing the
 * moment the form had buttons to show was the one screen in the area that did
 * not carry it. It is also the only place the restriction is stated -- the
 * subtitle says what the page is FOR.
 */
function PageHead({ children }: { children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Settings</h1>
        <div className="page-sub">Base configuration for the platform</div>
      </div>
      <div className="page-actions">
        <span className="badge pill-admin">
          <Shield aria-hidden="true" /> Admin only
        </span>
        {children}
      </div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card settings-card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <StateCard
        warning
        icon={<ShieldAlert aria-hidden="true" />}
        title="Platform Admins only"
      >
        <p>
          The platform configuration is available to Platform Admins. The server refuses anyone
          else, and records the refusal in the audit log.
        </p>
      </StateCard>
    </>
  );
}

/** Distinguishable from a form with no fields: bars, not inputs, and busy. */
function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="tabs settings-tabs" aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className="tab settings-tab-skeleton">
            <span className="skeleton-bar" />
          </span>
        ))}
      </div>
      <div className="card settings-card" aria-busy="true" aria-label="Loading settings">
        <div className="card-head">
          <span className="skeleton-bar settings-skeleton-title" />
        </div>
        <div className="card-body">
          <div className="grid grid-2">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="settings-skeleton-field">
                <span className="skeleton-bar settings-skeleton-label" />
                <span className="settings-skeleton-control" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// --- The screen -------------------------------------------------------------

export function ConfigurationPage() {
  const { canSee } = useSessionScope();
  // Presentation only: the navigation list is the server's decision, and the
  // server refuses the API calls regardless of what renders here.
  if (!canSee(NAV_KEY)) return <NoAccess />;
  return <ConfigurationScreen />;
}

export function ConfigurationScreen() {
  const catalog = useSettingsCatalog();

  if (catalog.isPending) return <LoadingSkeleton />;

  if (catalog.isError) {
    // A 403 is a refusal, not a failure -- and it never triggers the
    // interactive re-authentication a 401 does (f1.3).
    if (catalog.error instanceof ApiError && catalog.error.status === 403) return <NoAccess />;
    return (
      <>
        <PageHead />
        <StateCard
          warning
          icon={<AlertTriangle aria-hidden="true" />}
          title="The settings could not be loaded"
        >
          <p>{catalog.error instanceof Error ? catalog.error.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void catalog.refetch()}
              disabled={catalog.isFetching}
            >
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  if (catalog.data.sections.length === 0) {
    return (
      <>
        <PageHead />
        <StateCard icon={<SlidersHorizontal aria-hidden="true" />} title="No settings to configure">
          <p>
            No section holds an active setting yet. Settings arrive with the features that read
            them.
          </p>
        </StateCard>
      </>
    );
  }

  return <SettingsForm catalog={catalog.data} />;
}

type Banner =
  | { kind: 'saved'; count: number }
  | { kind: 'rejected'; message: string }
  | { kind: 'failed'; message: string };

export function SettingsForm({ catalog }: { catalog: SettingsCatalog }) {
  const save = useSaveSettings();
  const [draft, setDraft] = useState<Draft>({});
  const [serverErrors, setServerErrors] = useState<Readonly<Record<string, string>>>({});
  const [attempted, setAttempted] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [activeCode, setActiveCode] = useState<string>(catalog.sections[0]?.code ?? '');

  const active =
    catalog.sections.find((section) => section.code === activeCode) ?? catalog.sections[0];

  const changes = useMemo(() => pendingChanges(catalog, draft), [catalog, draft]);
  const perSection = useMemo(() => pendingBySection(catalog, draft), [catalog, draft]);
  const local = useMemo(() => localProblems(catalog, draft), [catalog, draft]);
  const pending = changes.length;
  const saving = save.isPending;

  useUnsavedChangesGuard(pending > 0, LEAVE_PROMPT);

  /** A server error wins; a local one shows once a save was attempted or the field was left. */
  const problemFor = (key: string): string | null =>
    serverErrors[key] ?? (attempted || key in draft ? local[key] ?? null : null);

  const sectionHasProblem = (code: string): boolean => {
    const section = catalog.sections.find((candidate) => candidate.code === code);
    return Boolean(section?.settings.some((setting) => problemFor(setting.key)));
  };

  const edit = (key: string, value: string | boolean) => {
    setDraft((current) => ({ ...current, [key]: value }));
    if (key in serverErrors) {
      // The field changed since the server refused it, so its message is stale.
      setServerErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
    setBanner((current) => (current?.kind === 'saved' ? null : current));
  };

  const discard = () => {
    setDraft({});
    setServerErrors({});
    setAttempted(false);
    setBanner(null);
  };

  const submit = () => {
    if (pending === 0 || saving) return;
    setAttempted(true);

    const blocking = Object.keys(local);
    if (blocking.length > 0) {
      const section = firstSectionWithProblem(catalog, blocking);
      if (section) setActiveCode(section);
      setBanner({
        kind: 'rejected',
        message: 'Some values are not valid yet. Correct the highlighted fields and save again.',
      });
      return;
    }

    save.mutate(changes, {
      onSuccess: () => {
        setDraft({});
        setServerErrors({});
        setAttempted(false);
        setBanner({ kind: 'saved', count: changes.length });
      },
      onError: (error) => {
        if (error instanceof ApiError && error.errors.length > 0) {
          const byKey = errorsByKey(error.errors);
          setServerErrors(byKey);
          const section = firstSectionWithProblem(catalog, Object.keys(byKey));
          if (section) setActiveCode(section);
          setBanner({ kind: 'rejected', message: error.message });
          return;
        }
        setBanner({
          kind: 'failed',
          message: error instanceof Error ? error.message : 'The settings could not be saved.',
        });
      },
    });
  };

  // Only reached with at least one section (the screen renders an empty state
  // otherwise); the guard satisfies the type checker, after every hook.
  if (!active) return null;

  const ActiveIcon = sectionIcon(active.icon_name);

  return (
    <>
      <PageHead>
        {pending > 0 && (
          <span className="settings-pending" aria-live="polite">
            <span className="settings-pending-dot" aria-hidden="true" />
            {pending} unsaved {pending === 1 ? 'change' : 'changes'}
          </span>
        )}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={discard}
          disabled={pending === 0 || saving}
        >
          <RotateCcw aria-hidden="true" /> Discard
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={pending === 0 || saving}
        >
          {saving ? (
            <Loader2 aria-hidden="true" className="spin" />
          ) : (
            <Check aria-hidden="true" />
          )}
          Save changes
          {pending > 0 && <span className="btn-count">{pending}</span>}
        </button>
      </PageHead>

      {banner && <SettingsBanner banner={banner} />}

      <div className="tabs settings-tabs" role="tablist" aria-label="Settings sections">
        {catalog.sections.map((section) => {
          const Icon = sectionIcon(section.icon_name);
          const selected = section.code === active.code;
          const count = perSection[section.code] ?? 0;
          const flagged = sectionHasProblem(section.code);
          return (
            <button
              key={section.code}
              type="button"
              role="tab"
              id={`settings-tab-${section.code}`}
              aria-selected={selected}
              aria-controls={`settings-panel-${section.code}`}
              className={`tab${selected ? ' is-active' : ''}`}
              onClick={() => setActiveCode(section.code)}
            >
              <Icon aria-hidden="true" /> {section.label}
              {flagged ? (
                <span className="count settings-count-problem" aria-label="has problems">
                  !
                </span>
              ) : (
                count > 0 && (
                  <span className="count" aria-label={`${count} unsaved`}>
                    {count}
                  </span>
                )
              )}
            </button>
          );
        })}
      </div>

      <section
        key={active.code}
        className="card settings-card settings-panel"
        role="tabpanel"
        id={`settings-panel-${active.code}`}
        aria-labelledby={`settings-tab-${active.code}`}
      >
        <div className="card-head">
          <h3 className="settings-card-title">
            <span className="settings-card-icon" aria-hidden="true">
              <ActiveIcon />
            </span>
            {active.label}
          </h3>
        </div>
        <div className="card-body">
          {active.description && <p className="settings-section-desc">{active.description}</p>}
          <div className="grid grid-2">
            {active.settings.map((setting) => (
              <SettingField
                key={setting.key}
                setting={setting}
                draft={draft}
                changed={isChanged(setting, draft)}
                problem={problemFor(setting.key)}
                disabled={saving}
                onChange={edit}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function SettingsBanner({ banner }: { banner: Banner }) {
  if (banner.kind === 'saved') {
    return (
      <div className="alert alert-success settings-banner" role="status">
        <CheckCircle2 aria-hidden="true" />
        <div>
          <div className="a-title">Saved</div>
          <div className="a-body">
            {banner.count} {banner.count === 1 ? 'setting was' : 'settings were'} updated. The
            change is recorded in the audit log.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="alert alert-danger settings-banner" role="alert">
      <XCircle aria-hidden="true" />
      <div>
        <div className="a-title">
          {banner.kind === 'rejected' ? 'Nothing was saved' : 'The settings could not be saved'}
        </div>
        <div className="a-body">{banner.message}</div>
      </div>
    </div>
  );
}
